import {
  keepPreviousData,
  QueryClient,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useMemo } from "react";

import { MainDealersQueryKeys } from "./mainDealersQueryKeys";
import { getMainDealers } from "@/api/mainDealers";
import { NewUser } from "@/types/ApiResponses/User";
import { CacheUpdater, createListOps } from "@/utils/queryCache";

export const useMainDealers = (options: { enabled: boolean }) => {
  return useQuery({
    queryKey: [MainDealersQueryKeys.All],
    placeholderData: keepPreviousData,
    enabled: options ? options.enabled : true,
    queryFn: ({ signal }) => {
      const params = {};
      return getMainDealers({ signal, params });
    },
  });
};

interface MainDealersManager {
  get: () => NewUser[] | undefined;
  replace: (data: NewUser[]) => void;
  patchRow: (id: number, updater: CacheUpdater<NewUser>) => void;
  replaceRow: (row: NewUser) => void;
  invalidate: () => void;
}

export const createMainDealersManager = (
  queryClient: QueryClient
): MainDealersManager => {
  const ops = createListOps<NewUser>(
    queryClient,
    [MainDealersQueryKeys.All],
    (a, b) => a.id === b.id
  );
  return {
    get: () => ops.get(),
    replace: (data) => ops.replace(data),
    // ListOps identify rows by id, so a bare {id} carrier is enough.
    patchRow: (id, updater) => ops.patch({ id } as NewUser, updater),
    replaceRow: (row) => ops.addOrUpdate(row),
    invalidate: () => ops.invalidate(),
  };
};

export const useMainDealersManager = (): MainDealersManager => {
  const queryClient = useQueryClient();
  return useMemo(() => createMainDealersManager(queryClient), [queryClient]);
};
